import React from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    Input,
    Row,
    Spinner
} from "reactstrap";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminServicePlanTemplateDetail extends React.Component {
    state = {
        isLoading: false,
        template: null,
        unauthorized: false,
        editName: "",
        editDescription: "",
        saving: false,
        saveSuccess: false,
        saveError: null,
        togglingActive: false,
        items: [],
        itemsChanged: false,
        savingItems: false,
        itemsSaveSuccess: false,
        itemsSaveError: null,
        newItemTitle: "",
        newItemNotes: ""
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (parsed?.user?.isAdmin) {
                this.loadTemplate();
            } else {
                this.setState({ unauthorized: true });
            }
        } catch {
            this.setState({ unauthorized: true });
        }
    }

    loadTemplate = () => {
        const { templateId } = this.props.params;
        this.setState({ isLoading: true });
        Api.get(`/api/admin/service-plan-templates/${templateId}`)
            .then(({ data }) => this.setState({
                template: data,
                editName: data.name || "",
                editDescription: data.description || "",
                items: data.items || [],
                itemsChanged: false
            }))
            .catch(() => this.setState({ saveError: "Unable to load template." }))
            .finally(() => this.setState({ isLoading: false }));
    };

    recalibrateItems = (items) => items.map((item, index) => ({ ...item, displayOrder: index }));

    handleMetadataSave = (e) => {
        e.preventDefault();
        const { templateId } = this.props.params;
        this.setState({ saving: true, saveSuccess: false, saveError: null });
        Api.put(`/api/admin/service-plan-templates/${templateId}`, {
            name: this.state.editName,
            description: this.state.editDescription
        })
            .then(({ data }) => this.setState({
                template: data,
                editName: data.name || "",
                editDescription: data.description || "",
                saveSuccess: true
            }))
            .catch(err => this.setState({
                saveError: err.response?.data?.message || "Unable to save template details."
            }))
            .finally(() => this.setState({ saving: false }));
    };

    handleToggleActive = () => {
        const { template, togglingActive } = this.state;
        if (!template || togglingActive) return;
        this.setState({ togglingActive: true });
        Api.patch(`/api/admin/service-plan-templates/${template.id}/active`, { active: !template.active })
            .then(({ data }) => this.setState({ template: data }))
            .catch(() => this.setState({ saveError: "Unable to update active status." }))
            .finally(() => this.setState({ togglingActive: false }));
    };

    handleAddItem = (e) => {
        e.preventDefault();
        const title = this.state.newItemTitle.trim();
        if (!title) return;
        const items = this.recalibrateItems([
            ...this.state.items,
            {
                id: `new-${Date.now()}`,
                displayOrder: this.state.items.length,
                title,
                notes: this.state.newItemNotes,
                itemType: "SERVICE_PART"
            }
        ]);
        this.setState({ items, itemsChanged: true, newItemTitle: "", newItemNotes: "" });
    };

    handleRemoveItem = (index) => {
        const items = this.recalibrateItems(this.state.items.filter((_, i) => i !== index));
        this.setState({ items, itemsChanged: true });
    };

    handleMoveItem = (index, direction) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= this.state.items.length) return;
        const items = [...this.state.items];
        const item = items[index];
        items[index] = items[nextIndex];
        items[nextIndex] = item;
        this.setState({ items: this.recalibrateItems(items), itemsChanged: true });
    };

    handleEditItemField = (index, field, value) => {
        const items = this.state.items.map((item, i) => i === index ? { ...item, [field]: value } : item);
        this.setState({ items, itemsChanged: true });
    };

    handleSaveItems = () => {
        const { templateId } = this.props.params;
        const itemsPayload = this.state.items.map((item, index) => ({
            displayOrder: index,
            title: item.title,
            notes: item.notes,
            itemType: item.itemType || "SERVICE_PART"
        }));
        this.setState({ savingItems: true, itemsSaveSuccess: false, itemsSaveError: null });
        Api.put(`/api/admin/service-plan-templates/${templateId}/items`, itemsPayload)
            .then(({ data }) => this.setState({
                template: data,
                items: data.items || [],
                itemsChanged: false,
                itemsSaveSuccess: true
            }))
            .catch(err => this.setState({
                itemsSaveError: err.response?.data?.message || "Unable to save template items."
            }))
            .finally(() => this.setState({ savingItems: false }));
    };

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const {
            isLoading,
            template,
            editName,
            editDescription,
            saving,
            saveSuccess,
            saveError,
            togglingActive,
            items,
            itemsChanged,
            savingItems,
            itemsSaveSuccess,
            itemsSaveError,
            newItemTitle,
            newItemNotes
        } = this.state;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Service Plan Template<br /><span>{template?.name || "edit template"}</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-5">
                                        <Button color="link" onClick={() => this.props.navigate("/admin/service-plan-templates")} className="mb-3 p-0">
                                            &larr; Back to templates
                                        </Button>

                                        {isLoading && <div className="text-center py-4"><Spinner color="primary" /></div>}
                                        {!isLoading && template && (
                                            <>
                                                {saveSuccess && <Alert color="success">Template details saved.</Alert>}
                                                {saveError && <Alert color="danger">{saveError}</Alert>}

                                                <Form onSubmit={this.handleMetadataSave} className="mb-4">
                                                    <Row>
                                                        <Col md="4" className="mb-2">
                                                            <Input
                                                                value={editName}
                                                                onChange={e => this.setState({ editName: e.target.value })}
                                                                placeholder="Template name"
                                                            />
                                                        </Col>
                                                        <Col md="6" className="mb-2">
                                                            <Input
                                                                value={editDescription}
                                                                onChange={e => this.setState({ editDescription: e.target.value })}
                                                                placeholder="Description"
                                                            />
                                                        </Col>
                                                        <Col md="2" className="mb-2">
                                                            <Button color="primary" type="submit" disabled={saving || !editName.trim()} block>
                                                                {saving ? "Saving..." : "Save"}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                <div className="mb-4">
                                                    <Badge color={template.active ? "success" : "secondary"} className="mr-2">
                                                        {template.active ? "Active (visible to users)" : "Inactive (hidden from users)"}
                                                    </Badge>
                                                    <Button color={template.active ? "warning" : "success"} size="sm" onClick={this.handleToggleActive} disabled={togglingActive}>
                                                        {template.active ? "Deactivate" : "Activate"}
                                                    </Button>
                                                </div>

                                                <h5>Template Items</h5>
                                                {itemsSaveSuccess && <Alert color="success">Template items saved.</Alert>}
                                                {itemsSaveError && <Alert color="danger">{itemsSaveError}</Alert>}

                                                {items.map((item, index) => (
                                                    <Card key={item.id || index} className="mb-3 border">
                                                        <CardBody>
                                                            <Row className="align-items-start">
                                                                <Col md="2" className="mb-2">
                                                                    <Button color="secondary" size="sm" onClick={() => this.handleMoveItem(index, -1)} disabled={index === 0} className="mr-1">
                                                                        Up
                                                                    </Button>
                                                                    <Button color="secondary" size="sm" onClick={() => this.handleMoveItem(index, 1)} disabled={index === items.length - 1}>
                                                                        Down
                                                                    </Button>
                                                                </Col>
                                                                <Col md="4" className="mb-2">
                                                                    <Input
                                                                        value={item.title || ""}
                                                                        onChange={e => this.handleEditItemField(index, "title", e.target.value)}
                                                                        placeholder="Service element title"
                                                                    />
                                                                </Col>
                                                                <Col md="5" className="mb-2">
                                                                    <Input
                                                                        type="textarea"
                                                                        rows="2"
                                                                        value={item.notes || ""}
                                                                        onChange={e => this.handleEditItemField(index, "notes", e.target.value)}
                                                                        placeholder="Optional notes"
                                                                    />
                                                                </Col>
                                                                <Col md="1" className="mb-2 text-right">
                                                                    <Button color="danger" size="sm" onClick={() => this.handleRemoveItem(index)}>
                                                                        Remove
                                                                    </Button>
                                                                </Col>
                                                            </Row>
                                                        </CardBody>
                                                    </Card>
                                                ))}

                                                <Form onSubmit={this.handleAddItem} className="mt-4">
                                                    <Row>
                                                        <Col md="4" className="mb-2">
                                                            <Input
                                                                value={newItemTitle}
                                                                onChange={e => this.setState({ newItemTitle: e.target.value })}
                                                                placeholder="New service element title"
                                                            />
                                                        </Col>
                                                        <Col md="6" className="mb-2">
                                                            <Input
                                                                value={newItemNotes}
                                                                onChange={e => this.setState({ newItemNotes: e.target.value })}
                                                                placeholder="Optional notes"
                                                            />
                                                        </Col>
                                                        <Col md="2" className="mb-2">
                                                            <Button color="secondary" type="submit" disabled={!newItemTitle.trim()} block>
                                                                Add Item
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                <Button color="primary" onClick={this.handleSaveItems} disabled={!itemsChanged || savingItems} className="mt-3">
                                                    {savingItems ? "Saving Items..." : "Save Items"}
                                                </Button>
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }
}

function withRouterParamsAndNavigate(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}

export default withRouterParamsAndNavigate(AdminServicePlanTemplateDetail);
